import React from "react";
import { motion } from "framer-motion";
import { Recycle, Trash2, Leaf, Sparkles, ArrowRight } from "lucide-react";

const WasteManagementPreloader = ({
  size = "default",
  message = "Loading...",
  showMessage = true,
  fullScreen = false,
  variant = "default", // "default" or "minimal"
}) => {
  const sizeClasses = {
    small: "w-16 h-16",
    default: "w-24 h-24",
    large: "w-32 h-32",
    xl: "w-40 h-40",
  };

  const containerSize = sizeClasses[size] || sizeClasses.default;

  // Minimal variant for subtle loading
  if (variant === "minimal") {
    return (
      <div className={`flex items-center justify-center ${fullScreen ? "fixed inset-0 z-50 bg-white/80 backdrop-blur-sm" : "p-4"}`}>
        <div className="flex flex-col items-center gap-3">
          <motion.div
            className="relative"
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <Recycle className="w-8 h-8 text-green-600" />
          </motion.div>
          {showMessage && (
            <motion.p
              className="text-sm font-medium text-gray-600"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              {message}
            </motion.p>
          )}
        </div>
      </div>
    );
  }

  const PreloaderContent = () => (
    <div className="flex flex-col items-center justify-center gap-6">
      {/* Main Animation Container */}
      <div className={`relative ${containerSize}`}>
        {/* Outer Rotating Ring with Gradient */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(from 0deg, #10b981, #059669, #047857, #065f46, #10b981)`,
            mask: "radial-gradient(circle, transparent 60%, black 60%)",
            WebkitMask: "radial-gradient(circle, transparent 60%, black 60%)",
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Middle Rotating Ring (Counter-clockwise) */}
        <motion.div
          className="absolute inset-3 rounded-full"
          style={{
            background: `conic-gradient(from 180deg, #34d399, #10b981, #059669, #047857, #34d399)`,
            mask: "radial-gradient(circle, transparent 65%, black 65%)",
            WebkitMask: "radial-gradient(circle, transparent 65%, black 65%)",
          }}
          animate={{ rotate: -360 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Inner Pulsing Circle with Glow */}
        <motion.div
          className="absolute inset-6 rounded-full bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 shadow-lg"
          style={{
            boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Recycling Symbol with Glow */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative"
            animate={{
              rotate: 360,
              scale: [1, 1.1, 1],
            }}
            transition={{
              rotate: {
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              },
              scale: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          >
            <Recycle className="w-8 h-8 md:w-10 md:h-10 text-white drop-shadow-lg" />
            <motion.div
              className="absolute inset-0"
              animate={{
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Recycle className="w-8 h-8 md:w-10 md:h-10 text-emerald-200 blur-sm" />
            </motion.div>
          </motion.div>
        </div>

        {/* Orbiting Waste Particles */}
        {[...Array(8)].map((_, i) => {
          const angle = (i * Math.PI * 2) / 8;
          const radius = 35;
          return (
            <motion.div
              key={i}
              className="absolute w-2.5 h-2.5 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-md"
              style={{
                top: "50%",
                left: "50%",
                originX: 0.5,
                originY: 0.5,
              }}
              animate={{
                x: [
                  Math.cos(angle) * radius,
                  Math.cos(angle + Math.PI) * radius,
                  Math.cos(angle) * radius,
                ],
                y: [
                  Math.sin(angle) * radius,
                  Math.sin(angle + Math.PI) * radius,
                  Math.sin(angle) * radius,
                ],
                opacity: [0.4, 1, 0.4],
                scale: [0.8, 1.3, 0.8],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.15,
              }}
            />
          );
        })}

        {/* Sparkle Effects */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`sparkle-${i}`}
            className="absolute"
            style={{
              top: `${15 + i * 25}%`,
              left: `${15 + i * 25}%`,
            }}
            animate={{
              rotate: 360,
              scale: [0, 1.2, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.4,
            }}
          >
            <Sparkles className="w-3 h-3 text-emerald-400 drop-shadow-md" />
          </motion.div>
        ))}
      </div>

      {/* Secondary Icons Animation with Waste Flow */}
      <div className="relative flex items-center gap-6 px-4">
        {[
          { icon: Trash2, delay: 0, color: "text-red-500" },
          { icon: ArrowRight, delay: 0.4, color: "text-gray-400" },
          { icon: Recycle, delay: 0.8, color: "text-green-500" },
          { icon: ArrowRight, delay: 1.2, color: "text-gray-400" },
          { icon: Leaf, delay: 1.6, color: "text-emerald-500" },
        ].map(({ icon: Icon, delay, color }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0.4, 1, 0.4],
              scale: [0.9, 1.1, 0.9],
              y: [0, -8, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: delay,
            }}
            className="relative"
          >
            <Icon className={`w-5 h-5 ${color}`} />
            {index < 4 && (
              <motion.div
                className="absolute -right-3 top-1/2 transform -translate-y-1/2"
                animate={{
                  x: [0, 5, 0],
                  opacity: [0.3, 0.7, 0.3],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: delay + 0.2,
                }}
              >
                <div className="w-1 h-1 bg-green-400 rounded-full"></div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Loading Message with Progress Dots */}
      {showMessage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center space-y-3"
        >
          <motion.p
            className="text-lg md:text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {message}
          </motion.p>
          <div className="flex gap-2 justify-center">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className="w-2.5 h-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full shadow-md"
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.4, 1, 0.4],
                  y: [0, -4, 0],
                }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.15,
                }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="relative"
        >
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-full blur-3xl -z-10"></div>
          <PreloaderContent />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="flex items-center justify-center p-8">
      <PreloaderContent />
    </div>
  );
};

export default WasteManagementPreloader;
